import React from "react";
import { canonicalAdminStatus } from "./adminStatusUi";

const MIN_MS = 60_000;
const H_MS = 3_600_000;
const D_MS = 86_400_000;

/** Orange from 10h without change; red strictly after 24h (same for New & Waiting response). */
const IDLE_ORANGE_MS = 10 * H_MS;
const IDLE_RED_MS = D_MS;

function parseAnchorMs(iso) {
  if (!iso) return NaN;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : NaN;
}

/** Best-effort instant when current status phase started (list rows may omit updatedAt). */
export function getRequestStatusIdleAnchorMs(row) {
  const st = canonicalAdminStatus(row?.status);
  if (st === "confirmed" || st === "rejected") return null;

  if (st === "new") {
    return parseAnchorMs(row?.createdAt);
  }

  if (st === "waiting_response") {
    const u = parseAnchorMs(row?.updatedAt);
    const c = parseAnchorMs(row?.createdAt);
    return Number.isFinite(u) ? u : c;
  }

  const u = parseAnchorMs(row?.updatedAt);
  return Number.isFinite(u) ? u : parseAnchorMs(row?.createdAt);
}

export function formatIdleDurationShort(ms) {
  const mins = Math.floor(ms / MIN_MS);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(ms / H_MS);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  if (rh === 0) return `${d} d`;
  return `${d}d ${rh}h`;
}

function idleLevel(idleMs) {
  if (idleMs > IDLE_RED_MS) return "stale";
  if (idleMs >= IDLE_ORANGE_MS) return "warn";
  return "fresh";
}

function idleTitle(canonicalStatus, anchorMs, idleMs) {
  const since = new Date(anchorMs).toLocaleString();
  const dur = formatIdleDurationShort(idleMs);
  const rule =
    "Green: under 10 h. Orange: 10 h–24 h without change. Red: more than 1 day.";
  if (canonicalStatus === "new") {
    return `Still "New" — no pipeline change since submitted (${since}). About ${dur} idle. ${rule}`;
  }
  return `Waiting on client / follow-up — last activity around ${since} (~${dur} in this stage). ${rule}`;
}

/**
 * @returns {{ show: false } | { show: true, level: 'fresh'|'warn'|'stale', label: string, title: string }}
 */
export function getRequestStatusIdleInfo(row, nowMs = Date.now()) {
  const st = canonicalAdminStatus(row?.status);
  if (st === "confirmed" || st === "rejected") {
    return { show: false };
  }

  const anchor = getRequestStatusIdleAnchorMs(row);
  if (!Number.isFinite(anchor)) {
    return { show: false };
  }

  const idleMs = Math.max(0, nowMs - anchor);
  const level = idleLevel(idleMs);

  return {
    show: true,
    level,
    label: formatIdleDurationShort(idleMs),
    title: idleTitle(st, anchor, idleMs),
  };
}

/** Parent should bump state on an interval if labels should refresh each minute. */
export function RequestStatusIdleCell({ row }) {
  const info = getRequestStatusIdleInfo(row);

  if (!info.show) {
    return <span className="admin-dash__idle-muted">—</span>;
  }

  return (
    <span
      className={`admin-dash__idle-pill admin-dash__idle-pill--${info.level}`}
      title={info.title}
      role="status"
      aria-label={info.title}
    >
      <span className="admin-dash__idle-dot" aria-hidden />
      <span className="admin-dash__idle-label">{info.label}</span>
    </span>
  );
}
